import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Progress,
  Row,
  Space,
  Tag,
  Typography,
  Statistic,
  Spin,
} from "antd";
import {
  BarChartOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DollarCircleOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  HeartOutlined,
  LineChartOutlined,
  PlayCircleOutlined,
  ShareAltOutlined,
  TeamOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useUserStore } from "../../../stores/userStore";
import { useState } from "react";
import Router from "../..";
import { useSocialData } from "../../hooks/useSocialData";
import { formatCnt } from "../../utils/formatCnt";
import { CATEGORY_MAPPING, CATEGORY_STYLES } from "../../utils/categories";
import dayjs from "dayjs";

const { Title, Paragraph } = Typography;

const formatWatchTime = (seconds) => {
  const total = Math.round(Number(seconds) || 0);
  const min = Math.floor(total / 60);
  const sec = total % 60;
  return `${min}분 ${sec}초`;
};

const YOUTUBE_AUTH_URL =
  "https://accounts.google.com/o/oauth2/v2/auth?client_id=469105054059-5m3jmk34r3h9pjgnnbvddglmvj7vqbb4.apps.googleusercontent.com&redirect_uri=https://app.surfingcre.com/auth-success/&response_type=code&scope=https://www.googleapis.com/auth/youtube.readonly%20https://www.googleapis.com/auth/yt-analytics.readonly%20https://www.googleapis.com/auth/yt-analytics-monetary.readonly&access_type=offline&prompt=consent";

export const YoutubeTabContent = () => {
  const { userInfo } = useUserStore();
  const [youtubeRegisterLoading, setYoutubeRegisterLoading] = useState(false);
  const { data: youtubeData, isLoading } = useSocialData(
    "youtube",
    userInfo?.type_info?.youtube_influencer_id
  );

  const influencerInfo = youtubeData?.influencer_info ?? {};
  const categories = youtubeData?.category ?? [];
  const recentVideos = influencerInfo?.recent_videos ?? [];
  const audienceAges = influencerInfo?.audience_ages ?? [];
  const audienceGenders = influencerInfo?.audience_genders ?? [];
  const audienceCountries = influencerInfo?.audience_countries ?? [];

  // 팝업 인증 처리 함수
  const handleYoutubeAuth = () => {
    const popup = window.open(
      YOUTUBE_AUTH_URL,
      "youtubeAuthPopup",
      "width=500,height=700"
    );
    let handled = false;

    const timer = setInterval(async () => {
      try {
        if (!popup || popup.closed) {
          clearInterval(timer);
          return;
        }
        const url = popup.location.href;
        if (
          !handled &&
          url.startsWith("https://app.surfingcre.com/auth-success/")
        ) {
          handled = true;
          const params = new URL(url).searchParams;
          const code = params.get("code");
          if (code) {
            clearInterval(timer);
            setYoutubeRegisterLoading(true);
            const registered = await Router.Api.registerYoutubeCode(code);
            if (registered == null) {
              setYoutubeRegisterLoading(false);
              return;
            }
            popup.close();
            window.location.href = "/influencer/dashboard?tab=youtube";
          }
        }
      } catch (e) {
        console.error(e);
      }
    }, 500);
  };

  return (
    <div className="w-full max-w-[1212.5px] p-4 bg-white rounded-tr-[10px] rounded-bl-[10px] rounded-br-[10px] border border-[#d9d9d9] shadow-[0_2px_6px_rgba(0,0,0,0.1)] overflow-x-hidden">
      {isLoading || youtubeRegisterLoading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {userInfo?.type_info?.youtube_influencer_id === null ? (
            <div className="flex flex-col items-center py-10 px-5 text-center">
              <Space direction="vertical" size="large" className="w-full">
                <div>
                  <Title
                    level={3}
                    className="mb-2 text-[#262626] text-[20px] sm:text-[24px] md:text-[28px] lg:text-[32px]"
                  >
                    유튜브 계정을 연동해주세요
                  </Title>
                  <Paragraph className="text-[14px] sm:text-[16px] md:text-[18px] text-[#595959] mb-0">
                    유튜브 채널을 연동하여 데이터를 확인할 수 있습니다
                  </Paragraph>
                  <Paragraph className="text-[12px] sm:text-[13px] md:text-[14px] text-[#F5222D] mt-2 mb-0">
                    연동 진행 중 구글 인증 화면에서 요청되는 권한(유튜브
                    조회, 애널리틱스, 수익 애널리틱스)에 모두 동의해주셔야
                    정상적으로 연동됩니다. 일부 권한에 동의하지 않으면
                    서비스 이용이 어려울 수 있습니다.
                  </Paragraph>
                </div>

                <Button
                  type="primary"
                  size="large"
                  onClick={handleYoutubeAuth}
                  className="
                !bg-[#FF0000]
                border-0
                hover:opacity-90
                h-12 sm:h-14 md:h-16
                text-[16px] sm:text-[18px] md:text-[20px]
                font-semibold
                px-8 sm:px-10 md:px-12
                rounded-lg
                shadow-[0_2px_12px_rgba(255,0,0,0.35)]
              "
                >
                  유튜브 인증하기
                </Button>

                <Divider className="my-8 text-[14px] sm:text-[16px] md:text-[18px]">
                  인증 후 이용 가능한 기능
                </Divider>

                <Row gutter={[24, 24]}>
                  <Col xs={24} md={8}>
                    <Card
                      hoverable
                      className="text-center rounded-xl border border-[#e8e8e8] h-full"
                      styles={{ body: { padding: "20px" } }}
                    >
                      <BarChartOutlined className="text-[32px] sm:text-[40px] md:text-[48px] text-[#FF0000] mb-3" />
                      <div className="text-[14px] sm:text-[16px] md:text-[18px] font-semibold text-[#262626]">
                        채널 통계 확인
                      </div>
                      <div className="text-[12px] sm:text-[14px] md:text-[16px] text-[#8c8c8c] mt-1">
                        구독자, 조회수 등
                      </div>
                    </Card>
                  </Col>

                  <Col xs={24} md={8}>
                    <Card
                      hoverable
                      className="text-center rounded-xl border border-[#e8e8e8] h-full"
                      styles={{ body: { padding: "20px" } }}
                    >
                      <LineChartOutlined className="text-[32px] sm:text-[40px] md:text-[48px] text-[#FF0000] mb-3" />
                      <div className="text-[14px] sm:text-[16px] md:text-[18px] font-semibold text-[#262626]">
                        데이터 분석
                      </div>
                      <div className="text-[12px] sm:text-[14px] md:text-[16px] text-[#8c8c8c] mt-1">
                        조회수, 시청 지표 등
                      </div>
                    </Card>
                  </Col>

                  <Col xs={24} md={8}>
                    <Card
                      hoverable
                      className="text-center rounded-xl border border-[#e8e8e8] h-full"
                    >
                      <PlayCircleOutlined className="text-[32px] sm:text-[40px] md:text-[48px] text-[#FF0000] mb-3" />
                      <div className="text-[14px] sm:text-[16px] md:text-[18px] font-semibold text-[#262626]">
                        영상 성과 확인
                      </div>
                      <div className="text-[12px] sm:text-[14px] md:text-[16px] text-[#8c8c8c] mt-1">
                        업로드 영상별 성과
                      </div>
                    </Card>
                  </Col>
                </Row>
              </Space>
            </div>
          ) : (
            <div>
              <Card className="mb-6 rounded-xl border border-[#E8E8E8]">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-12">
                  <Avatar
                    src={influencerInfo.profile_img}
                    className="!w-[72px] !h-[72px] md:!w-[96px] md:!h-[96px] border-4 border-[#F0F0F0]"
                  />

                  <div className="min-w-0">
                    {categories.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1">
                        {categories.map((category, idx) => (
                          <Tag
                            key={idx}
                            className="border-none px-2 py-0.5 text-[11px] md:text-[12px] font-medium rounded-lg m-0"
                            style={
                              CATEGORY_STYLES[category] || CATEGORY_STYLES.etc
                            }
                          >
                            {CATEGORY_MAPPING[category] || category}
                          </Tag>
                        ))}
                      </div>
                    )}

                    <p className="m-0 text-[18px] md:text-[22px] font-bold leading-tight break-words">
                      {influencerInfo.nickname || "-"}
                    </p>

                    <p className="text-[#8C8C8C] m-0 text-[12px] md:text-[13px] break-words">
                      @{influencerInfo.unique_id || "-"}
                      {influencerInfo.custom_url
                        ? ` · ${influencerInfo.custom_url}`
                        : ""}
                      {influencerInfo.region ? ` · ${influencerInfo.region}` : ""}
                    </p>

                    {influencerInfo.bio && (
                      <p className="text-[#595959] text-[12px] md:text-[13px] mt-2 leading-relaxed break-words">
                        {influencerInfo.bio}
                      </p>
                    )}
                  </div>
                </div>
              </Card>

              {/* 채널 통계 (youtube.readonly / yt-analytics.readonly) */}
              <div className="mb-8">
                <Card
                  title="채널 통계"
                  className="rounded-xl border border-[#E8E8E8]"
                  styles={{ body: { padding: "24px" } }}
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            구독자
                          </span>
                        }
                        value={influencerInfo.follower_count}
                        prefix={
                          <UserOutlined className="text-[#5B8FF9] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            {formatCnt(value)}
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            총 영상
                          </span>
                        }
                        value={influencerInfo.video_count}
                        prefix={
                          <VideoCameraOutlined className="text-[#722ED1] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            {value}
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            총 조회수
                          </span>
                        }
                        value={influencerInfo.view_count}
                        prefix={
                          <EyeOutlined className="text-[#13C2C2] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            {formatCnt(value)}
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            총 좋아요
                          </span>
                        }
                        value={influencerInfo.like_count}
                        prefix={
                          <HeartOutlined className="text-[#FF4D4F] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            {formatCnt(value)}
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            평균 조회수
                          </span>
                        }
                        value={influencerInfo.average_views}
                        prefix={
                          <EyeOutlined className="text-[#13C2C2] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            {formatCnt(value)}
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            평균 좋아요
                          </span>
                        }
                        value={influencerInfo.average_likes}
                        prefix={
                          <HeartOutlined className="text-[#FF4D4F] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            {formatCnt(value)}
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            평균 댓글 수
                          </span>
                        }
                        value={influencerInfo.average_comments}
                        prefix={
                          <CommentOutlined className="text-[#1890FF] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            {formatCnt(value)}
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            평균 공유 수
                          </span>
                        }
                        value={influencerInfo.average_shares}
                        prefix={
                          <ShareAltOutlined className="text-[#52C41A] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            {formatCnt(value)}
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            평균 시청 시간
                          </span>
                        }
                        value={formatWatchTime(
                          influencerInfo.average_watch_time
                        )}
                        prefix={
                          <ClockCircleOutlined className="text-[#722ED1] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            {value}
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            평균 시청 비율
                          </span>
                        }
                        value={(
                          Number(influencerInfo.average_view_percentage) || 0
                        ).toFixed(1)}
                        prefix={
                          <FieldTimeOutlined className="text-[#13C2C2] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            {value}%
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            최근 신규 구독
                          </span>
                        }
                        value={influencerInfo.subscribers_gained}
                        prefix={
                          <TeamOutlined className="text-[#52C41A] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            +{formatCnt(value)}
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            최근 구독 취소
                          </span>
                        }
                        value={influencerInfo.subscribers_lost}
                        prefix={
                          <TeamOutlined className="text-[#FF4D4F] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            -{formatCnt(value)}
                          </span>
                        )}
                      />
                    </Col>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            참여율
                          </span>
                        }
                        value={(Number(influencerInfo.engagement) || 0).toFixed(
                          2
                        )}
                        prefix={
                          <BarChartOutlined className="text-[#52C41A] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold text-[#52C41A]">
                            {value}%
                          </span>
                        )}
                      />
                    </Col>
                  </Row>
                </Card>
              </div>

              {/* 오디언스 분석 (yt-analytics.readonly) */}
              <div className="mb-8">
                <Card
                  title="오디언스 분석"
                  className="rounded-xl border border-[#E8E8E8]"
                  styles={{ body: { padding: "24px" } }}
                >
                  <Row gutter={[24, 24]}>
                    <Col xs={24} md={8}>
                      <p className="text-[13px] text-[#8C8C8C] mb-3">
                        연령대
                      </p>
                      {audienceAges.length > 0 ? (
                        <Space direction="vertical" className="w-full">
                          {audienceAges.map((row, idx) => (
                            <div key={idx}>
                              <div className="flex justify-between text-[12px] mb-1">
                                <span>{row.ageGroup}</span>
                                <span>
                                  {(row.viewerPercentage || 0).toFixed(1)}%
                                </span>
                              </div>
                              <Progress
                                percent={row.viewerPercentage || 0}
                                showInfo={false}
                                strokeColor="#F9A43F"
                              />
                            </div>
                          ))}
                        </Space>
                      ) : (
                        <Empty description="데이터 없음" />
                      )}
                    </Col>
                    <Col xs={24} md={8}>
                      <p className="text-[13px] text-[#8C8C8C] mb-3">성별</p>
                      {audienceGenders.length > 0 ? (
                        <Space direction="vertical" className="w-full">
                          {audienceGenders.map((row, idx) => (
                            <div key={idx}>
                              <div className="flex justify-between text-[12px] mb-1">
                                <span>{row.gender}</span>
                                <span>
                                  {(row.viewerPercentage || 0).toFixed(1)}%
                                </span>
                              </div>
                              <Progress
                                percent={row.viewerPercentage || 0}
                                showInfo={false}
                                strokeColor="#5B8FF9"
                              />
                            </div>
                          ))}
                        </Space>
                      ) : (
                        <Empty description="데이터 없음" />
                      )}
                    </Col>
                    <Col xs={24} md={8}>
                      <p className="text-[13px] text-[#8C8C8C] mb-3">
                        국가별 조회수 (상위 10개국)
                      </p>
                      {audienceCountries.length > 0 ? (
                        <Space direction="vertical" className="w-full">
                          {audienceCountries.map((row, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between text-[12px]"
                            >
                              <span>{row.country}</span>
                              <span>{formatCnt(row.views)}</span>
                            </div>
                          ))}
                        </Space>
                      ) : (
                        <Empty description="데이터 없음" />
                      )}
                    </Col>
                  </Row>
                </Card>
              </div>

              {/* 수익 분석 (yt-analytics-monetary.readonly) */}
              <div className="mb-8">
                <Card
                  title="수익 분석"
                  className="rounded-xl border border-[#E8E8E8]"
                  styles={{ body: { padding: "24px" } }}
                >
                  <Row gutter={[16, 16]}>
                    <Col xs={12} lg={8}>
                      <Statistic
                        title={
                          <span className="text-[13px] text-[#8C8C8C]">
                            예상 수익 (USD)
                          </span>
                        }
                        value={(
                          Number(influencerInfo.estimated_revenue) || 0
                        ).toFixed(2)}
                        prefix={
                          <DollarCircleOutlined className="text-[#FAAD14] text-[22px] md:text-[24px]" />
                        }
                        formatter={(value) => (
                          <span className="text-[22px] md:text-[24px] font-semibold">
                            ${value}
                          </span>
                        )}
                      />
                    </Col>
                  </Row>
                </Card>
              </div>

              {/* 최근 영상 (youtube.readonly) */}
              <div>
                <p className="text-[18px] font-semibold text-[#262626] mb-4 pb-3 border-b-2 border-[#F0F0F0]">
                  최근 영상
                </p>
                {recentVideos.length > 0 ? (
                  <Row gutter={[16, 16]}>
                    {recentVideos.map((video) => (
                      <Col xs={24} sm={12} lg={8} key={video.video_id}>
                        <Card
                          hoverable
                          cover={
                            video.thumbnail_url ? (
                              <img
                                src={video.thumbnail_url}
                                alt={video.title}
                                className="w-full h-[160px] object-cover"
                              />
                            ) : null
                          }
                          styles={{ body: { padding: "12px" } }}
                          className="rounded-xl border border-[#E8E8E8] overflow-hidden"
                        >
                          <p className="m-0 text-[13px] font-semibold line-clamp-2">
                            {video.title}
                          </p>
                          <p className="m-0 text-[11px] text-[#8C8C8C] mt-1">
                            {video.publish_time
                              ? dayjs(video.publish_time).format(
                                  "YYYY.MM.DD"
                                )
                              : "-"}
                          </p>
                          <div className="flex gap-3 mt-2 text-[11px] text-[#595959]">
                            <span>
                              <EyeOutlined /> {formatCnt(video.view_count)}
                            </span>
                            <span>
                              <HeartOutlined /> {formatCnt(video.like_count)}
                            </span>
                            <span>
                              <CommentOutlined />{" "}
                              {formatCnt(video.comment_count)}
                            </span>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Empty description="최근 업로드된 영상이 없습니다" />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
